import { type OperationPendingRow } from '@utazzo/shared';
import { UzDatePicker, UzInput, UzSelect } from '@utazzo/ui';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDateDMY, formatInrPaise, isoToLocalDate, toDateInputIso } from '../invoices.helpers';
import { useOperationPending, useInvoiceAgents } from '../use-invoices';

import {
  ReportListScreen,
  type ReportColumn,
} from '@/components/report-list-screen/ReportListScreen';

/**
 * Operation Pending - report #33, a 1:1 port of operationpendingreport.aspx:
 * future-check-in bookings that are partially paid (ops follow-up), ordered by
 * check-in date. Built on the shared ReportListScreen.
 */

const MONTHS = [
  { value: '', label: 'All months' },
  ...['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(
    (m, i) => ({ value: String(i + 1), label: m }),
  ),
];
const thisYear = new Date().getFullYear();
const YEARS = [
  { value: '', label: 'All years' },
  ...Array.from({ length: 6 }, (_, i) => {
    const y = thisYear + 1 - i;
    return { value: String(y), label: String(y) };
  }),
];

const sumPaise = (
  rows: ReadonlyArray<OperationPendingRow>,
  pick: (r: OperationPendingRow) => string,
) => formatInrPaise(rows.reduce((acc, r) => acc + BigInt(pick(r)), 0n).toString());

export const OperationPendingPage = (): React.JSX.Element => {
  const [checkInFrom, setCheckInFrom] = useState('');
  const [checkInTo, setCheckInTo] = useState('');
  const [paxName, setPaxName] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [agent, setAgent] = useState('');

  const query = {
    ...(checkInFrom ? { checkInFrom } : {}),
    ...(checkInTo ? { checkInTo } : {}),
    ...(paxName.trim() ? { paxName: paxName.trim() } : {}),
    ...(bookingId.trim() ? { bookingId: bookingId.trim() } : {}),
    ...(month ? { month: Number(month) } : {}),
    ...(year ? { year: Number(year) } : {}),
    ...(agent ? { agent } : {}),
  };
  const reportQuery = useOperationPending(query);
  const rows = reportQuery.data?.rows ?? [];

  const agentsQuery = useInvoiceAgents();
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...(agentsQuery.data ?? []).map((a) => ({ value: a, label: a })),
  ];

  const columns: ReportColumn<OperationPendingRow>[] = [
    {
      header: 'Proforma No.',
      value: (r) => r.proformaNumber ?? '',
      render: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="text-primary hover:underline">
          {r.proformaNumber ?? '-'}
        </Link>
      ),
    },
    { header: 'Pax Name', value: (r) => r.paxName ?? '' },
    { header: 'Destination', value: (r) => r.destination ?? '' },
    {
      header: 'Balance Amt.',
      align: 'right',
      className: 'font-medium text-red-600',
      value: (r) => Number(r.outstandingPaise) / 100,
      render: (r) => formatInrPaise(r.outstandingPaise),
      total: (rs) => sumPaise(rs, (r) => r.outstandingPaise),
    },
    { header: 'Check In', value: (r) => (r.checkInDate ? formatDateDMY(r.checkInDate) : '----') },
    {
      header: 'Check Out',
      value: (r) => (r.checkOutDate ? formatDateDMY(r.checkOutDate) : '----'),
    },
    { header: 'Agent', value: (r) => r.agentName ?? '' },
  ];

  return (
    <ReportListScreen
      title="Operation Pending"
      description="Future check-in bookings that are partially paid and still owe a balance."
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.invoiceId}
      loading={reportQuery.isLoading}
      error={reportQuery.isError}
      emptyMessage="No pending operations match these filters."
      exportName="operation-pending"
      filters={
        <>
          <UzDatePicker
            label="Check-in from"
            value={isoToLocalDate(checkInFrom)}
            onValueChange={(d) => setCheckInFrom(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzDatePicker
            label="Check-in to"
            value={isoToLocalDate(checkInTo)}
            onValueChange={(d) => setCheckInTo(d ? toDateInputIso(d) : '')}
            containerClassName="w-40"
          />
          <UzInput
            label="Pax name"
            value={paxName}
            onChange={(e) => setPaxName(e.target.value)}
            containerClassName="w-44"
          />
          <UzInput
            label="Proforma No."
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            containerClassName="w-40"
          />
          <UzSelect
            label="Month"
            value={month}
            onValueChange={setMonth}
            options={MONTHS}
            containerClassName="w-36"
          />
          <UzSelect
            label="Year"
            value={year}
            onValueChange={setYear}
            options={YEARS}
            containerClassName="w-32"
          />
          <UzSelect
            label="Agent"
            value={agent}
            onValueChange={setAgent}
            options={agentOptions}
            containerClassName="w-44"
          />
        </>
      }
    />
  );
};
